import { Link } from "react-router-dom";

import { InfoIcon, WarningIcon } from "./Icons";

type Props = {
  agentId: string;
  /** ISO timestamp of when the dashboard row was created — i.e. when the
   * enrollment was confirmed. Compared against now to decide whether the
   * agent is still in the happy-path window or stuck. */
  createdAt: string;
  /** True when this notice is rendered inside a clickable parent (list
   * card). In that case we don't render our own Link to /computers/<id>
   * because the parent already navigates there on click — a nested Link
   * would intercept the click and break the back-button. */
  withinClickableParent?: boolean;
};

// Time thresholds for the stuck-detection nudge. A real enrollment that
// finishes successfully transitions to ONLINE within ~60 s of the first
// heartbeat firing. Two minutes is past the happy-path window; five
// minutes means something is actually wrong.
const STUCK_AFTER_MS = 2 * 60 * 1000;
const URGENT_AFTER_MS = 5 * 60 * 1000;

/** Renders the "Setting up..." line under a provisioning agent card.
 *
 * Audit follow-up: the old version always showed the optimistic
 * "this usually takes less than a minute" copy, even when the agent
 * had been stuck in provisioning for hours. Customers had no signal
 * that something was wrong and no path to the Resend Setup recovery
 * button on the detail page. This component escalates the copy based
 * on how long it's been since the dashboard row was created.
 */
export default function ProvisioningNotice({
  agentId,
  createdAt,
  withinClickableParent = false,
}: Props) {
  const ageMs = Date.now() - new Date(createdAt).getTime();

  if (ageMs < STUCK_AFTER_MS) {
    return <OptimisticPanel />;
  }

  return (
    <StuckPanel
      agentId={agentId}
      urgent={ageMs >= URGENT_AFTER_MS}
      ageMinutes={Math.floor(ageMs / 60000)}
      withinClickableParent={withinClickableParent}
    />
  );
}

function OptimisticPanel() {
  return (
    <div className="mt-3 flex items-center gap-2 text-sm text-amber-700">
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      Setting up — this usually takes less than a minute.
    </div>
  );
}

function StuckPanel({
  agentId,
  urgent,
  ageMinutes,
  withinClickableParent,
}: {
  agentId: string;
  urgent: boolean;
  ageMinutes: number;
  withinClickableParent: boolean;
}) {
  const palette = urgent
    ? {
        wrapper: "bg-red-50 text-red-800",
        icon: <InfoIcon />,
        heading: "Setup isn't finishing",
        body:
          "This computer hasn't checked in. The agent may be installed but can't reach " +
          "KraetdNet — usually firewall, antivirus, or a stale certificate.",
        cta: "Open this computer to see the error + try Resend Setup",
      }
    : {
        wrapper: "bg-amber-50 text-amber-800",
        icon: <WarningIcon />,
        heading: "Taking longer than expected",
        body: `Still setting up after ${ageMinutes} minute${ageMinutes === 1 ? "" : "s"}. Open this computer to see what the agent reported and try Resend Setup.`,
        cta: "Open this computer to see details",
      };

  return (
    <div className={`mt-3 flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${palette.wrapper}`}>
      <div className="mt-0.5 shrink-0">{palette.icon}</div>
      <div className="min-w-0">
        <div className="font-medium">{palette.heading}</div>
        <p className="mt-0.5 text-xs opacity-90">{palette.body}</p>
        <Link
          to={`/computers/${agentId}`}
          // Stop propagation so a click on this Link inside a clickable
          // card doesn't ALSO trigger the card's onClick navigate. Both
          // go to the same /computers/<id> URL, so the duplicate
          // wouldn't cause visible breakage — but it would push two
          // entries onto the history stack and break the back button.
          onClick={(e) => {
            if (withinClickableParent) e.stopPropagation();
          }}
          className="mt-1.5 inline-block text-xs font-medium underline hover:opacity-80"
        >
          {palette.cta} →
        </Link>
      </div>
    </div>
  );
}
